import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { peso, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const prStatusStyle = {
  "Draft": "bg-slate-100 text-slate-600", "Pending Approval": "bg-amber-50 text-amber-700",
  "Approved": "bg-emerald-50 text-emerald-700", "Rejected": "bg-red-50 text-red-700",
  "RFQ": "bg-blue-50 text-blue-700", "PO Issued": "bg-indigo-50 text-indigo-700",
  "Completed": "bg-emerald-50 text-emerald-700",
};
const STAGES = ["Pending Approval", "Approved", "RFQ", "PO Issued", "Completed"];

function Pipeline({ status }) {
  const idx = STAGES.indexOf(status === "Rejected" ? "Pending Approval" : status);
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {["Request", "Approval", "RFQ", "PO", "Receiving"].map((s, i) => (
        <React.Fragment key={s}>
          <span className={`px-2 py-0.5 rounded text-[11px] ${i <= idx ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>{s}</span>
          {i < 4 && <Icons.ChevronRight className="h-3 w-3 text-slate-300" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Procurement() {
  const { hasPerm } = useAuth();
  const canApprove = hasPerm("procurement.approve");
  const canPO = hasPerm("procurement.purchase_order");
  const [mine, setMine] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rfqSheet, setRfqSheet] = useState(null);
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: 0 }]);
  const [form, setForm] = useState({ title: "", justification: "" });
  const [quote, setQuote] = useState({ supplier_id: "", total: 0, lead_time_days: 7, remarks: "" });
  const [supForm, setSupForm] = useState({ name: "", category: "", email: "", phone: "" });

  const load = useCallback(() => {
    api.get("/procurement/requests?scope_q=mine").then(({ data }) => setMine(data));
    api.get("/procurement/requests?scope_q=approvals").then(({ data }) => setApprovals(data)).catch(() => {});
    api.get("/procurement/rfqs").then(({ data }) => setRfqs(data)).catch(() => {});
    api.get("/procurement/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/procurement/suppliers").then(({ data }) => setSuppliers(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.unit_price || 0), 0);

  const createPR = async () => {
    try {
      await api.post("/procurement/requests", { ...form, items: items.map((i) => ({ description: i.description, qty: Number(i.qty), unit_price: Number(i.unit_price) })) });
      toast.success("Purchase request submitted");
      setOpen(false); setForm({ title: "", justification: "" }); setItems([{ description: "", qty: 1, unit_price: 0 }]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const actPR = async (pr, action) => {
    try { await api.post(`/procurement/requests/${pr.id}/${action}`, { reason: "" }); toast.success(`PR ${action}d`); setDetail(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const makeRFQ = async (pr) => {
    try { await api.post(`/procurement/requests/${pr.id}/rfq`); toast.success("RFQ created"); setDetail(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addQuote = async () => {
    try { await api.post(`/procurement/rfqs/${rfqSheet.id}/quotation`, { ...quote, total: Number(quote.total), lead_time_days: Number(quote.lead_time_days) }); toast.success("Quotation added"); const { data } = await api.get("/procurement/rfqs"); setRfqs(data); setRfqSheet(data.find((r) => r.id === rfqSheet.id)); setQuote({ supplier_id: "", total: 0, lead_time_days: 7, remarks: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const award = async (rfq, supplierId) => {
    try { await api.post(`/procurement/rfqs/${rfq.id}/award`, { reason: supplierId }); toast.success("Awarded — PO created"); setRfqSheet(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const receive = async (po) => {
    try { await api.post(`/procurement/orders/${po.id}/receive`, { reason: "" }); toast.success("PO received"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addSupplier = async () => {
    try { await api.post("/procurement/suppliers", supForm); toast.success("Supplier added"); setSupForm({ name: "", category: "", email: "", phone: "" }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const PRTable = ({ rows, showApprove }) => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead>Reference</TableHead><TableHead>Title</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right"></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No records.</TableCell></TableRow>}
          {rows.map((pr) => (
            <TableRow key={pr.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(pr)} data-testid={`pr-row-${pr.reference}`}>
              <TableCell className="font-mono text-xs">{pr.reference}</TableCell>
              <TableCell className="text-sm">{pr.title}</TableCell>
              <TableCell className="font-mono text-sm">{peso(pr.total_amount)}</TableCell>
              <TableCell><Badge className={`${prStatusStyle[pr.status]} hover:opacity-100 text-[10px]`}>{pr.status}</Badge></TableCell>
              <TableCell className="text-right">
                {showApprove && pr.status === "Pending Approval" && (
                  <div className="flex gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => actPR(pr, "approve")} data-testid={`pr-approve-${pr.reference}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => actPR(pr, "reject")}>Reject</Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Procurement" subtitle="Purchase requests → approval → RFQ → PO → receiving"
        actions={hasPerm("procurement.create") && <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-pr-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> New Request</Button>} />

      <Tabs defaultValue="requests">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="requests" data-testid="proc-tab-requests">My Requests</TabsTrigger>
          {canApprove && <TabsTrigger value="approvals" data-testid="proc-tab-approvals">Approvals {approvals.length > 0 && <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] h-4">{approvals.length}</Badge>}</TabsTrigger>}
          <TabsTrigger value="rfqs" data-testid="proc-tab-rfqs">RFQs</TabsTrigger>
          <TabsTrigger value="orders" data-testid="proc-tab-orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="proc-tab-suppliers">Suppliers</TabsTrigger>
        </TabsList>
        <TabsContent value="requests"><PRTable rows={mine} /></TabsContent>
        {canApprove && <TabsContent value="approvals"><PRTable rows={approvals} showApprove /></TabsContent>}
        <TabsContent value="rfqs">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>RFQ</TableHead><TableHead>Title</TableHead><TableHead>Quotes</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {rfqs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No RFQs.</TableCell></TableRow>}
                {rfqs.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setRfqSheet(r)} data-testid={`rfq-row-${r.reference}`}>
                    <TableCell className="font-mono text-xs">{r.reference}</TableCell>
                    <TableCell className="text-sm">{r.title}</TableCell>
                    <TableCell className="text-sm">{r.quotations?.length || 0}</TableCell>
                    <TableCell><Badge className={`${r.status === "Awarded" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"} hover:opacity-100 text-[10px]`}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right"><Icons.ChevronRight className="h-4 w-4 text-slate-300" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="orders">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>PO</TableHead><TableHead>Supplier</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No purchase orders.</TableCell></TableRow>}
                {orders.map((po) => (
                  <TableRow key={po.id} data-testid={`po-row-${po.reference}`}>
                    <TableCell className="font-mono text-xs">{po.reference}</TableCell>
                    <TableCell className="text-sm">{po.supplier_name}</TableCell>
                    <TableCell className="font-mono text-sm">{peso(po.total_amount)}</TableCell>
                    <TableCell><Badge className={`${po.received ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-700"} hover:opacity-100 text-[10px]`}>{po.status}</Badge></TableCell>
                    <TableCell className="text-right">{canPO && !po.received && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => receive(po)} data-testid={`po-receive-${po.reference}`}>Mark Received</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="suppliers">
          <div className="grid lg:grid-cols-3 gap-5">
            {hasPerm("procurement.create") && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 h-fit">
                <div className="text-sm font-medium text-slate-800 mb-4">Add Supplier</div>
                <div className="space-y-3">
                  <div className="space-y-2"><Label>Name</Label><Input value={supForm.name} onChange={(e) => setSupForm({ ...supForm, name: e.target.value })} data-testid="supplier-name" /></div>
                  <div className="space-y-2"><Label>Category</Label><Input value={supForm.category} onChange={(e) => setSupForm({ ...supForm, category: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Email</Label><Input value={supForm.email} onChange={(e) => setSupForm({ ...supForm, email: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={supForm.phone} onChange={(e) => setSupForm({ ...supForm, phone: e.target.value })} /></div>
                  <Button onClick={addSupplier} disabled={!supForm.name} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="supplier-create">Add Supplier</Button>
                </div>
              </div>
            )}
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b text-sm font-medium text-slate-800">Suppliers ({suppliers.length})</div>
              <div className="divide-y divide-slate-100">
                {suppliers.map((s) => (
                  <div key={s.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><Icons.Factory className="h-5 w-5" /></div>
                    <div className="flex-1"><div className="text-sm font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-500">{s.category} · {s.email}</div></div>
                    <Badge variant="outline" className="font-mono text-xs">{s.code}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* New PR dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Purchase Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pr-title" /></div>
            <div className="space-y-2">
              <Label>Line Items</Label>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-6" placeholder="Description" value={it.description} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} data-testid={`pr-item-desc-${i}`} />
                  <Input className="col-span-2" type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
                  <Input className="col-span-3" type="number" placeholder="Unit ₱" value={it.unit_price} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
                  <button className="col-span-1 text-red-500" onClick={() => setItems(items.filter((_, j) => j !== i))}><Icons.X className="h-4 w-4" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setItems([...items, { description: "", qty: 1, unit_price: 0 }])} data-testid="pr-add-item"><Icons.Plus className="h-4 w-4 mr-1" /> Add Item</Button>
            </div>
            <div className="space-y-2"><Label>Justification</Label><Textarea value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} /></div>
            <div className="bg-slate-50 rounded-lg px-4 py-3 flex justify-between items-center"><span className="text-sm text-slate-600">Total</span><span className="font-mono font-semibold text-blue-700">{peso(total)}</span></div>
            <p className="text-xs text-slate-400">Approval route is determined automatically by the Approval Matrix based on amount.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={createPR} disabled={!form.title || total <= 0} className="bg-blue-600 hover:bg-blue-700" data-testid="pr-submit">Submit Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PR detail drawer */}
      <Sheet open={!!detail} onOpenChange={() => setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle>{detail.reference}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <Pipeline status={detail.status} />
                <div><div className="text-lg font-semibold text-slate-900">{detail.title}</div><div className="text-sm text-slate-500">{detail.department} · {detail.requester_name}</div></div>
                <div className="bg-slate-50 rounded-lg p-3">
                  {detail.items.map((it, i) => (<div key={i} className="flex justify-between text-sm py-1"><span className="text-slate-600">{it.description} × {it.qty}</span><span className="font-mono">{peso(it.amount)}</span></div>))}
                  <div className="flex justify-between border-t mt-1 pt-2 font-semibold"><span>Total</span><span className="font-mono text-blue-700">{peso(detail.total_amount)}</span></div>
                </div>
                {detail.approval_chain?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Approval Route</div>
                    {detail.approval_chain.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5">
                        <span className="text-slate-700">{i + 1}. {s.role} <span className="text-slate-400">({s.approver_name})</span></span>
                        <Badge className={`text-[10px] ${s.status === "Approved" ? "bg-emerald-50 text-emerald-700" : s.status === "Rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"} hover:opacity-100`}>{s.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                {detail.status === "Approved" && !detail.rfq_id && hasPerm("procurement.create") && (
                  <Button onClick={() => makeRFQ(detail)} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="create-rfq-button"><Icons.FileSpreadsheet className="h-4 w-4 mr-1.5" /> Create RFQ</Button>
                )}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">History</div>
                  {detail.history?.map((h, i) => (<div key={i} className="text-xs text-slate-500 py-1">{h.action} — {h.by} · {fmtDateTime(h.at)}</div>))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* RFQ sheet */}
      <Sheet open={!!rfqSheet} onOpenChange={() => setRfqSheet(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {rfqSheet && (
            <>
              <SheetHeader><SheetTitle>{rfqSheet.reference} — {rfqSheet.title}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Quotations</div>
                {(rfqSheet.quotations || []).length === 0 && <div className="text-sm text-slate-400">No quotations yet.</div>}
                {(rfqSheet.quotations || []).map((qt) => (
                  <div key={qt.id} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                    <div><div className="text-sm font-medium text-slate-800">{qt.supplier_name}</div><div className="text-xs text-slate-500">{qt.lead_time_days} days lead · {peso(qt.total)}</div></div>
                    {rfqSheet.status === "Open" && canPO && <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => award(rfqSheet, qt.supplier_id)} data-testid={`award-${qt.supplier_id}`}>Award</Button>}
                  </div>
                ))}
                {rfqSheet.status === "Open" && hasPerm("procurement.create") && (
                  <div className="border-t pt-4 space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Add Quotation</div>
                    <Select value={quote.supplier_id} onValueChange={(v) => setQuote({ ...quote, supplier_id: v })}>
                      <SelectTrigger data-testid="quote-supplier"><SelectValue placeholder="Supplier" /></SelectTrigger>
                      <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" placeholder="Total ₱" value={quote.total} onChange={(e) => setQuote({ ...quote, total: e.target.value })} data-testid="quote-total" />
                      <Input type="number" placeholder="Lead days" value={quote.lead_time_days} onChange={(e) => setQuote({ ...quote, lead_time_days: e.target.value })} />
                    </div>
                    <Button onClick={addQuote} disabled={!quote.supplier_id || !quote.total} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="add-quote-button">Add Quotation</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
